import React, { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import { AppButton } from "../ui";
import { useAuth } from "../../context/AuthContext";

const HomeExperienceCard = ({
  card,
  onOpen,
  onJoinCode,
  onEdit,
  showEditButton = false,
}) => {
  const { hasClassroomAccess } = useAuth();
  const environmentCount = Array.isArray(card.environmentNames) ? card.environmentNames.length : 0;
  const topics = Array.isArray(card.topics) ? card.topics.filter(Boolean) : [];
  const visibleTopics = topics.slice(0, 3);
  const hiddenTopicCount = Math.max(topics.length - visibleTopics.length, 0);
  const [thumbnailLoadErrored, setThumbnailLoadErrored] = useState(false);

  useEffect(() => {
    setThumbnailLoadErrored(false);
  }, [card.id, card.thumbnailUrl]);

  const handleCardKeyDown = (event) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(card);
    }
  };

  return (
    <article
      className="homeExperienceCard"
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${card.title}`}
      onClick={() => onOpen(card)}
      onKeyDown={handleCardKeyDown}
    >
      <div className="homeExperienceCard__media">
        {showEditButton && onEdit && (
          <IconButton
            size="small"
            className="homeExperienceCard__editButton"
            aria-label={`Edit ${card.title}`}
            onClick={(event) => {
              event.stopPropagation();
              onEdit(card);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        {card.thumbnailUrl && !thumbnailLoadErrored ? (
          <img
            className="homeExperienceCard__image"
            src={card.thumbnailUrl}
            alt={`${card.title} thumbnail`}
            loading="lazy"
            onError={() => setThumbnailLoadErrored(true)}
          />
        ) : (
          <div className="homeExperienceCard__fallback">
            <span>{card.title}</span>
          </div>
        )}
      </div>

      <div className="homeExperienceCard__body">
        <h3 className="homeExperienceCard__title">{card.title}</h3>
        <p className="homeExperienceCard__description">{card.description}</p>
        {visibleTopics.length > 0 && (
          <ul className="homeExperienceCard__topics" aria-label="Topics">
            {visibleTopics.map((topic) => (
              <li key={topic} className="homeExperienceCard__topic">
                {topic}
              </li>
            ))}
            {hiddenTopicCount > 0 && (
              <li className="homeExperienceCard__topic homeExperienceCard__topic--overflow">
                +{hiddenTopicCount}
              </li>
            )}
          </ul>
        )}
        <p className="homeExperienceCard__meta">
          <span>Typical Session Length: {card.averageLength || "15 min"}</span>
          {environmentCount > 0 && <span>Environments: {environmentCount}</span>}
        </p>
        <div className="homeExperienceCard__actions">
          <AppButton
            component={Link}
            to={hasClassroomAccess ? card.viewerUrl : "/upgrade"}
            kind="primary"
            startIcon={<VisibilityOutlinedIcon />}
            className="homeExperienceCard__launch"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {hasClassroomAccess ? "Preview" : "Upgrade to Preview"}
          </AppButton>
          {hasClassroomAccess ? (
            <AppButton
              kind="secondary"
              className="homeExperienceCard__join"
              onClick={(event) => {
                event.stopPropagation();
                onJoinCode?.(card);
              }}
            >
              Join Code
            </AppButton>
          ) : (
            <AppButton
              component={Link}
              to="/upgrade"
              kind="secondary"
              className="homeExperienceCard__join"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              Upgrade for Join Codes
            </AppButton>
          )}
        </div>
      </div>
    </article>
  );
};

export default HomeExperienceCard;
